/* eslint-disable no-use-before-define */
/* eslint-disable @typescript-eslint/no-empty-function */
import React, {
  createContext,
  useReducer,
  useContext,
  ReactElement,
} from 'react';

import { Icon } from '@betrybe/ada-react';

type stateProps = {
  state: string;
  notificationClassName: string;
  notificationIcon: ReactElement;
  notificationTitle: string;
  notificationText: string;
};

type ToastNotificationProviderProps = {
  children: ReactElement;
  // eslint-disable-next-line react/require-default-props
  providedState?: stateProps;
};

export type actionProps = {
  type: string;
  payload: {
    title: string;
    text: string;
  };
};

const dispatchContextDefaultValues = {
  dispatch: () => {},
};

const stateDefaultValue = {
  state: 'closed',
  notificationClassName: '',
  notificationIcon: <> </>,
  notificationTitle: '',
  notificationText: '',
};

const ToastNotificationStateContext =
  createContext<stateProps>(stateDefaultValue);
const ToastNotificationDispatchContext = createContext<
  React.Dispatch<actionProps>
>(dispatchContextDefaultValues.dispatch);

const toastNotificationReducer = (
  state: stateProps,
  action: actionProps,
): stateProps => {
  switch (action.type) {
    case 'close': {
      return {
        ...state,
        state: 'closed',
        notificationClassName: '',
        notificationIcon: <> </>,
        notificationTitle: '',
        notificationText: '',
      };
    }
    case 'info': {
      return {
        ...state,
        state: 'info',
        notificationClassName: 'notification-info',
        notificationIcon: (
          <Icon
            name="info_outline"
            className="notification-info-icon"
            height={17.5}
            width={17.5}
          />
        ),
        notificationTitle: action.payload.title,
        notificationText: action.payload.text,
      };
    }
    case 'warning': {
      return {
        ...state,
        state: 'warning',
        notificationClassName: 'notification-warning',
        notificationIcon: (
          <Icon
            name="warning_outline"
            className="notification-warning-icon"
            height={17.5}
            width={17.5}
          />
        ),
        notificationTitle: action.payload.title,
        notificationText: action.payload.text,
      };
    }
    case 'error': {
      return {
        ...state,
        state: 'error',
        notificationClassName: 'notification-error',
        notificationIcon: (
          <Icon
            name="error_outline"
            className="notification-error-icon"
            height={17.5}
            width={17.5}
          />
        ),
        notificationTitle: action.payload.title,
        notificationText: action.payload.text,
      };
    }
    case 'success': {
      return {
        ...state,
        state: 'success',
        notificationClassName: 'notification-success',
        notificationIcon: (
          <Icon
            name="done_all"
            className="notification-success-icon"
            height={17.5}
            width={17.5}
          />
        ),
        notificationTitle: action.payload.title,
        notificationText: action.payload.text,
      };
    }
    default: {
      throw new Error(`Unhandled action: ${action.type}`);
    }
  }
};

const ToastNotificationProvider = ({
  children,
  providedState,
}: ToastNotificationProviderProps): ReactElement => {
  const defaultState = {
    state: 'closed',
    notificationClassName: '',
    notificationIcon: <> </>,
    notificationTitle: '',
    notificationText: '',
  };
  const initialState = providedState || defaultState;
  const [state, dispatch] = useReducer(toastNotificationReducer, initialState);

  return (
    <ToastNotificationStateContext.Provider value={state}>
      <ToastNotificationDispatchContext.Provider value={dispatch}>
        {children}
      </ToastNotificationDispatchContext.Provider>
    </ToastNotificationStateContext.Provider>
  );
};

const useToastNotificationState = () => {
  const context = useContext(ToastNotificationStateContext);

  if (context === undefined) {
    throw new Error(
      'useToastNotificationState must be used within a ToastNotificationProvider',
    );
  }

  return context;
};

const useToastNotificationDispatch = (): React.Dispatch<actionProps> => {
  const context = useContext(ToastNotificationDispatchContext);

  if (context === undefined) {
    throw new Error(
      'useToastNotificationDispatch must be used within a ToastNotificationProvider',
    );
  }

  return context;
};

const useToastNotification = (): [stateProps, React.Dispatch<actionProps>] => [
  useToastNotificationState(),
  useToastNotificationDispatch(),
];

export { ToastNotificationProvider, useToastNotification };
